/**
 * Manual-entry targets — the equipment the signed-in user can enter data on.
 *
 * One backend shape (GET /v1/twin/my-manual-entry-targets) drives BOTH the web and
 * mobile data-entry forms: the caller's assigned entities that have genealogy-role
 * bindings on a manual-entry-app-gateway Thing, each with the fields to capture. The
 * client renders an input per field and POSTs to /v1/things/{thing_id}/readings (the
 * same ingress as scanned sources); the capture-worker assembles the genealogy.
 */

import { api } from "@/lib/api-client";
import type { Paginated } from "./types";
import type { TwinBinding } from "./twin-bindings";

/** One capturable value on a target — derived from a genealogy-role tag binding. */
export interface ManualEntryField {
  /** tags.name — what readings[].metric must equal for the capture index to pick it up. */
  metric: string;
  /** Humanized genealogy role (e.g. "Casting produced"). */
  label: string;
  genealogy_role: string;
  /** Drives the input type + value-vs-value_text (e.g. "string_label", "analog_scalar"). */
  data_kind: string;
  unit: string;
  /** The backing tag's source ('manual' | 'ble' | …). 'ble' fields are captured from a
   *  BLE handheld probe; the app renders a Bluetooth capture affordance for them. */
  source?: string;
  /** source='ble' only — the handheld model (VCH|VMH|VMS) the app matches a connected probe against. */
  ble_device_model?: string;
  /** source='ble' only — which CSV notify-frame field carries the value (default 0). */
  ble_frame_field?: number;
}

/** One assigned equipment a user can enter data on (mirrors twin.ManualEntryTarget). */
export interface ManualEntryTarget {
  entity_id: string;
  entity_name: string;
  entity_kind: string;
  role: string;
  /** The manual-entry-app-gateway Thing to POST readings to. */
  thing_id: string;
  thing_slug: string;
  fields: ManualEntryField[];
}

export const myManualEntry = {
  /** The caller's assigned manual-entry targets + their capturable fields. */
  targets: () =>
    api.get<Paginated<ManualEntryTarget>>(`/v1/twin/my-manual-entry-targets`),
};

/** Body for adding an operator-keyed datapoint to an equipment. */
export interface ManualTagBody {
  name: string;
  unit?: string;
  data_kind: string;
  /** For data_kind='image' — capture options (compress off = full-quality NDT). */
  image_opts?: { compress: boolean; max_dimension?: number };
  /** '' | 'manual' (default) | 'ble'. 'ble' authors a handheld-probe datapoint (migration 0088). */
  source?: "manual" | "ble";
  /** Required when source='ble' — the handheld model (VCH|VMH|VMS) that feeds this tag. */
  ble_device_model?: "VCH" | "VMH" | "VMS";
  /** source='ble' only — which CSV notify-frame field carries the value (default 0). */
  ble_frame_field?: number;
}

/** Authoring an equipment's manual-entry datapoints (admin/equipment detail). */
export const manualTags = {
  /**
   * POST /v1/twin/entities/{entity_id}/manual-tags — atomically resolve/create
   * the org's single canonical manual-entry gateway, create a source='manual'
   * tag, and bind it to the entity. Returns the new binding. Replaces the old
   * three-call create (list-gateway → create-tag → create-binding) that raced
   * when an org had more than one manual gateway.
   */
  create: (entityId: string, body: ManualTagBody) =>
    api.post<TwinBinding>(`/v1/twin/entities/${entityId}/manual-tags`, body),
};
